import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { CookieService } from 'ngx-cookie-service';
import { environment } from '../../../../../environments/environment';
import { ApiMethodService } from '../../../../tools/services/api/method-api/apiMethod.service';
import { ActionBackOfficeResponse } from '../../../models/api.model';
import {
  CreateLineRichMenuPayload,
  DeleteLineRichMenuPayload,
  LineRichMenuActionResponse,
  LineRichMenuConfig,
  LineRichMenuCreateResponse,
  LineRichMenuListResponse,
  LineRichMenuUserActionPayload,
  LineRichMenuUserResponse,
  SaveLineRichMenuConfigPayload,
  UploadLineRichMenuImagePayload,
} from '../../../models/system-settings/line-rich-menu.model';

@Injectable({ providedIn: 'root' })
export class ApiLineRichMenuService {
  private readonly baseUrl = environment.apiPrivateUrl;
  private readonly cookieService = inject(CookieService);
  private readonly apiMethodService = inject(ApiMethodService);

  private get token(): string {
    return this.cookieService.get('token');
  }

  getRichMenuConfig(): Observable<LineRichMenuConfig> {
    return this.apiMethodService.request<LineRichMenuConfig>(
      '/LineLiff/getRichMenuConfig',
      'GET',
      this.baseUrl,
      {
        isConsoleLog: true,
        labelLog: 'getRichMenuConfig',
        token: this.token,
        headerOnly: false,
      }
    );
  }

  getRichMenus(): Observable<LineRichMenuListResponse> {
    return this.apiMethodService.request<LineRichMenuListResponse>(
      '/LineLiff/getRichMenus',
      'GET',
      this.baseUrl,
      {
        isConsoleLog: true,
        labelLog: 'getRichMenus',
        token: this.token,
        headerOnly: false,
      }
    );
  }

  getUserRichMenu(uid: string): Observable<LineRichMenuUserResponse> {
    return this.apiMethodService.request<LineRichMenuUserResponse>(
      '/LineLiff/getUserRichMenu',
      'GET',
      this.baseUrl,
      {
        isConsoleLog: true,
        labelLog: 'getUserRichMenu',
        token: this.token,
        headerOnly: false,
        params: { uid },
      }
    );
  }

  getRichMenuCreateTemplate(): Observable<{ success: boolean; template: unknown }> {
    return this.apiMethodService.request<{ success: boolean; template: unknown }>(
      '/LineLiff/getRichMenuCreateTemplate',
      'GET',
      this.baseUrl,
      {
        isConsoleLog: true,
        labelLog: 'getRichMenuCreateTemplate',
        token: this.token,
        headerOnly: false,
      }
    );
  }

  saveRichMenuConfig(payload: SaveLineRichMenuConfigPayload): Observable<ActionBackOfficeResponse> {
    return this.apiMethodService.request<ActionBackOfficeResponse>(
      '/LineLiff/saveRichMenuConfig',
      'POST',
      this.baseUrl,
      {
        isConsoleLog: true,
        labelLog: 'saveRichMenuConfig',
        token: this.token,
        headerOnly: false,
        body: payload,
      }
    );
  }

  createRichMenu(payload: CreateLineRichMenuPayload): Observable<LineRichMenuCreateResponse> {
    return this.apiMethodService.request<LineRichMenuCreateResponse>(
      '/LineLiff/createRichMenu',
      'POST',
      this.baseUrl,
      {
        isConsoleLog: true,
        labelLog: 'createRichMenu',
        token: this.token,
        headerOnly: false,
        body: payload,
      }
    );
  }

  uploadRichMenuImage(payload: UploadLineRichMenuImagePayload): Observable<LineRichMenuActionResponse> {
    return this.apiMethodService.request<LineRichMenuActionResponse>(
      '/LineLiff/uploadRichMenuImage',
      'POST',
      this.baseUrl,
      {
        isConsoleLog: true,
        labelLog: 'uploadRichMenuImage',
        token: this.token,
        headerOnly: false,
        body: payload,
      }
    );
  }

  ensureMemberRichMenu(payload: LineRichMenuUserActionPayload): Observable<LineRichMenuActionResponse> {
    return this.apiMethodService.request<LineRichMenuActionResponse>(
      '/LineLiff/ensureMemberRichMenu',
      'POST',
      this.baseUrl,
      {
        isConsoleLog: true,
        labelLog: 'ensureMemberRichMenu',
        token: this.token,
        headerOnly: false,
        body: payload,
      }
    );
  }

  linkRichMenu(payload: LineRichMenuUserActionPayload): Observable<LineRichMenuActionResponse> {
    return this.apiMethodService.request<LineRichMenuActionResponse>(
      '/LineLiff/linkRichMenu',
      'POST',
      this.baseUrl,
      {
        isConsoleLog: true,
        labelLog: 'linkRichMenu',
        token: this.token,
        headerOnly: false,
        body: payload,
      }
    );
  }

  unlinkRichMenu(payload: LineRichMenuUserActionPayload): Observable<LineRichMenuActionResponse> {
    return this.apiMethodService.request<LineRichMenuActionResponse>(
      '/LineLiff/unlinkRichMenu',
      'POST',
      this.baseUrl,
      {
        isConsoleLog: true,
        labelLog: 'unlinkRichMenu',
        token: this.token,
        headerOnly: false,
        body: payload,
      }
    );
  }

  deleteRichMenu(payload: DeleteLineRichMenuPayload): Observable<LineRichMenuActionResponse> {
    return this.apiMethodService.request<LineRichMenuActionResponse>(
      '/LineLiff/deleteRichMenu',
      'POST',
      this.baseUrl,
      {
        isConsoleLog: true,
        labelLog: 'deleteRichMenu',
        token: this.token,
        headerOnly: false,
        body: payload,
      }
    );
  }
}
